import React, { useMemo, useState } from "react";
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";

const { Search, TextArea } = Input;

const MOCK_PROJECTS = [
  {
    key: "proj-1",
    name: "Apex Business Park",
    type: "Commercial",
    location: "Gota, Ahmedabad",
    status: "Active",
  },
  {
    key: "proj-2",
    name: "Riverfront Residency",
    type: "Residential",
    location: "Thaltej, Ahmedabad",
    status: "Planning",
  },
  {
    key: "proj-3",
    name: "Skyline Mixed Use Tower",
    type: "Mixed Use",
    location: "SG Highway, Ahmedabad",
    status: "Archived",
  },
];

const STATUS_OPTIONS = ["Active", "Planning", "Archived"];
const TYPE_OPTIONS = ["Residential", "Commercial", "Mixed Use", "Industrial"];

const AllProjects = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState("All Statuses");
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const filteredData = useMemo(() => {
    return MOCK_PROJECTS.filter((p) => {
      const matchesSearch =
        !searchValue || p.name.toLowerCase().includes(searchValue.toLowerCase());
      const matchesStatus =
        status === "All Statuses" || p.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [searchValue, status]);

  const columns = [
    { title: "Project Name", dataIndex: "name", key: "name", },
    { title: "Type", dataIndex: "type", key: "type", width: 150 },
    { title: "Location", dataIndex: "location", key: "location", width: 200 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (value) => {
        if (value === "Active") return <Tag color="green">Active</Tag>;
        if (value === "Planning") return <Tag color="orange">Planning</Tag>;
        return <Tag>Archived</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            className="SaveGradiantBtn"
            onClick={() => navigate("/project-structure", { state: { project: record } })}
          >
            Edit Structure
          </Button>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log("Edit Project", record);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log("Delete Project", record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleCreate = async () => {
    const values = await form.validateFields();
    // eslint-disable-next-line no-console
    console.log("Create Project", values);
    setIsCreateOpen(false);
    form.resetFields();
  };

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="List and manage all projects">
              <h3>All Projects</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Projects</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <Button
              type="primary"
              className="SaveGradiantBtn"
              onClick={() => setIsCreateOpen(true)}
            >
              <span className="material-symbols-outlined">
add
</span> Create New Project
            </Button>
          </div>
        </div>
      </div>

      <section className="grid-sec tablegridsec">
        <div className="PR-body">
          <div className="po-good-header">
            <Space wrap size={10}>
              <div className="searchwrp">
                <Search
                  placeholder="Search projects..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  allowClear
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  style={{ width: 320 }}
                />
              </div>
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: 200 }}
                options={[
                  { label: "All Statuses", value: "All Statuses" },
                  ...STATUS_OPTIONS.map((s) => ({ label: s, value: s })),
                ]}
                placeholder="All Statuses"
              />
            </Space>
          </div>

          <div className="tablewrp">
            <Table
              rowKey="key"
              columns={columns}
              dataSource={filteredData}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `Showing ${range[0]}–${range[1]} of ${total} projects`,
              }}
              scroll={{ x: 1100 }}
            />
          </div>
        </div>
      </section>

      <Modal
        title="Create New Project"
        open={isCreateOpen}
        onCancel={() => {
          setIsCreateOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Project Name"
            name="projectName"
            rules={[{ required: true, message: "Project name is required" }]}
          >
            <Input placeholder="Enter project name" />
          </Form.Item>

          <Form.Item
            label="Project Type"
            name="projectType"
            rules={[{ required: true, message: "Project type is required" }]}
          >
            <Select
              placeholder="Select project type"
              options={TYPE_OPTIONS.map((t) => ({ label: t, value: t }))}
            />
          </Form.Item>

          <Form.Item
            label="Project Address / Location"
            name="projectLocation"
            rules={[{ required: true, message: "Project location is required" }]}
          >
            <Input placeholder="Enter full address" />
          </Form.Item>

          <Form.Item label="Project Description (Optional)" name="projectDescription">
            <TextArea rows={3} placeholder="Enter brief description" />
          </Form.Item>
        </Form>

        <div className="FormFooter" style={{padding:'0px', margin:'0px'}}>
         
            <Button type="primary" className="CancelGradiantBtn"
              onClick={() => {
                setIsCreateOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" className="SaveGradiantBtn" onClick={handleCreate}>
              Create Project
            </Button>
      </div>
      </Modal>
    </>
  );
};

export default AllProjects;